import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  ActionIcon,
  Menu,
  Modal,
  TextInput,
  NumberInput,
  Select,
  Textarea,
  Stack,
  LoadingOverlay,
  Image,
  Divider,
  Avatar,
  ThemeIcon,
  SimpleGrid,
  Card,
  Container,
  Pagination,
  Checkbox,
  Tabs,
  Alert,
  Progress,
  Tooltip,
  ScrollArea,
  RingProgress,
  Center,
} from "@mantine/core";
import {
  IconSearch,
  IconFilter,
  IconCheck,
  IconX,
  IconDotsVertical,
  IconEye,
  IconEdit,
  IconTrash,
  IconUpload,
  IconPhoto,
  IconMovie,
  IconShoppingCart,
  IconCalendarEvent,
  IconAlertCircle,
  IconClock,
  IconMapPin,
  IconUser,
  IconChartBar,
  IconMessageCircle,
  IconInfoCircle,
  IconArrowLeft,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import CreateProductModal from "../adminProductManagement/createProductModal";
import { API_BASE_URL } from "../../config/baseUrls";
// =============================================================================
// UTILITIES & CONSTANTS
// =============================================================================

const PROJECT_STATUS = {
  PENDING_APPROVAL: {
    color: "yellow",
    icon: <IconClock size={14} />,
    text: "Pending",
  },
  ACTIVE: { color: "green", icon: <IconCheck size={14} />, text: "Active" },
  REJECTED: { color: "red", icon: <IconX size={14} />, text: "Rejected" },
  FAILED: { color: "red", icon: <IconX size={14} />, text: "Failed" },
  SUCCESSFUL: {
    color: "blue",
    icon: <IconCheck size={14} />,
    text: "Successful",
  },
  CANCELLED: { color: "gray", icon: <IconX size={14} />, text: "Cancelled" },
  COMPLETED: {
    color: "teal",
    icon: <IconCheck size={14} />,
    text: "Completed",
  },
};

const PROJECT_TYPES = {
  JOINT_PURCHASE: { icon: IconShoppingCart, label: "Joint Purchase" },
  NEW_PRODUCT: { icon: IconPhoto, label: "New Product" },
  MOVIE_SCREENING: { icon: IconMovie, label: "Movie Screening" },
  EVENT: { icon: IconCalendarEvent, label: "Event" },
};

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

const StatusBadge = ({ status }) => {
  const config = PROJECT_STATUS[status] || {
    color: "gray",
    icon: null,
    text: status,
  };
  return (
    <Badge color={config.color} variant="light" leftSection={config.icon}>
      {config.text}
    </Badge>
  );
};

const ProjectTypeBadge = ({ type }) => {
  const config = PROJECT_TYPES[type] || { icon: IconInfoCircle, label: type };
  const Icon = config.icon;
  return (
    <Badge
      variant="outline"
      color="gray"
      leftSection={<Icon size={12} />}
      styles={{ root: { textTransform: "none" } }}
    >
      {config.label}
    </Badge>
  );
};

export const ImageUploadComponent = ({ onUpload, existingUrl, className }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(existingUrl);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = async () => {
      const base64 = reader.result.split(",")[1];
      try {
        const res = await fetch(
          `${API_BASE_URL}/auth/uploadImage`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
            },
            body: JSON.stringify({ image: base64, name: file.name }),
          },
        );
        const data = await res.json();
        if (data.url) {
          setPreview(data.url);
          onUpload(data.url);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setUploading(false);
      }
    };
  };

  return (
    <Box className={className}>
      {preview ? (
        <Box
          pos="relative"
          w="100%"
          h={160}
          bg="gray.1"
          style={{ borderRadius: 8, overflow: "hidden" }}
        >
          <Image src={preview} h="100%" w="100%" fit="cover" />
          <ActionIcon
            color="red"
            variant="filled"
            pos="absolute"
            bottom={10}
            right={10}
            onClick={() => {
              setPreview(null);
              onUpload("");
            }}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Box>
      ) : (
        <Button
          component="label"
          variant="outline"
          color="gray"
          fullWidth
          h={120}
          style={{ borderStyle: "dashed" }}
          loading={uploading}
        >
          <Stack gap={0} align="center">
            <IconUpload size={24} />
            <Text size="sm" mt={4}>
              Upload Image
            </Text>
          </Stack>
          <input type="file" hidden accept="image/*" onChange={handleFile} />
        </Button>
      )}
    </Box>
  );
};

// =============================================================================
// MAIN PAGE COMPONENT
// =============================================================================

export const ProjectManagement = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [filters, setFilters] = useState({
    status: "PENDING_APPROVAL",
    projectType: "",
    category: "",
    sortBy: "newest",
    search: "",
  });

  const { showSuccess, showError, showWarning } = useNotification();

  // --- DATA FETCHING ---
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch Projects
      const query = new URLSearchParams({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      const projRes = await fetch(
        `${API_BASE_URL}/admin/projects/pending?${query}`,
        { headers: getAuthHeaders() },
      );
      const projData = await projRes.json();

      if (projData.success) {
        setProjects(projData.data.projects);
        setPagination(projData.data.pagination);
      }

      // Fetch Stats
      const statRes = await fetch(
        `${API_BASE_URL}/admin/projects/stats`,
        { headers: getAuthHeaders() },
      );
      const statData = await statRes.json();
      if (statData.success) setStats(statData.data);
    } catch (e) {
      showError("Error", "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters, pagination.page]);

  // --- HANDLERS ---
  const handleBulkApprove = async () => {
    if (selectedProjects.length === 0)
      return showWarning("No Selection", "Select projects first");
    try {
      const res = await fetch(
        `${API_BASE_URL}/admin/projects/bulk-approve`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            projectIds: selectedProjects,
            adminNotes: "Bulk approval",
          }),
        },
      );
      const data = await res.json();
      if (data.success) {
        showSuccess("Success", `${data.data.approvedCount} projects approved`);
        setSelectedProjects([]);
        fetchData();
      } else showError("Failed", data.error?.message);
    } catch (e) {
      showError("Error", "Bulk action failed");
    }
  };

  return (
    <Stack gap="lg">
      {/* Header & Stats */}
      <Group justify="space-between" align="flex-end">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Voting & Ideas
          </Text>
          <Text size="sm" c="dimmed">
            Manage user submitted projects and campaigns
          </Text>
        </Box>
        {selectedProjects.length > 0 && (
          <Button
            color="green"
            leftSection={<IconCheck size={16} />}
            onClick={handleBulkApprove}
          >
            Approve Selected ({selectedProjects.length})
          </Button>
        )}
      </Group>

      {/* {stats && (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <Paper
            p="md"
            radius="md"
            withBorder
            className="flex items-center gap-4"
          >
            <ThemeIcon size="xl" radius="md" variant="light" color="blue">
              <IconClock size={24} />
            </ThemeIcon>
            <Box>
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                Pending
              </Text>
              <Text size="xl" fw={700}>
                {stats.overview.pendingProjects}
              </Text>
            </Box>
          </Paper>
          <Paper
            p="md"
            radius="md"
            withBorder
            className="flex items-center gap-4"
          >
            <ThemeIcon size="xl" radius="md" variant="light" color="red">
              <IconAlertCircle size={24} />
            </ThemeIcon>
            <Box>
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                Urgent
              </Text>
              <Text size="xl" fw={700}>
                {stats.summary?.urgentReview || 0}
              </Text>
            </Box>
          </Paper>
          <Paper
            p="md"
            radius="md"
            withBorder
            className="flex items-center gap-4"
          >
            <ThemeIcon size="xl" radius="md" variant="light" color="green">
              <IconCheck size={24} />
            </ThemeIcon>
            <Box>
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                Active
              </Text>
              <Text size="xl" fw={700}>
                {stats.overview.activeProjects}
              </Text>
            </Box>
          </Paper>
          <Paper
            p="md"
            radius="md"
            withBorder
            className="flex items-center gap-4"
          >
            <ThemeIcon size="xl" radius="md" variant="light" color="purple">
              <IconChartBar size={24} />
            </ThemeIcon>
            <Box>
              <Text size="xs" c="dimmed" fw={700} tt="uppercase">
                Approval Rate
              </Text>
              <Text size="xl" fw={700}>
                {stats.performance?.approvalRate || 0}%
              </Text>
            </Box>
          </Paper>
        </SimpleGrid>
      )} */}

      {/* Filters */}
      <Paper p="md" withBorder radius="md">
        <Group grow align="end">
          <Select
            label="Status"
            data={["PENDING_APPROVAL", "ACTIVE", "FAILED", "ALL"]}
            value={filters.status}
            onChange={(v) =>
              setFilters({ ...filters, status: v === "ALL" ? "" : v })
            }
          />
          <Select
            label="Type"
            data={["JOINT_PURCHASE", "NEW_PRODUCT", "MOVIE_SCREENING", "EVENT"]}
            value={filters.projectType}
            onChange={(v) => setFilters({ ...filters, projectType: v })}
            placeholder="All Types"
          />
          <Select
            label="Sort By"
            data={[
              { value: "newest", label: "Newest First" },
              { value: "oldest", label: "Oldest First" },
              { value: "ending_soon", label: "Ending Soon" },
              { value: "highest_target", label: "Highest Target" },
            ]}
            value={filters.sortBy}
            onChange={(v) => setFilters({ ...filters, sortBy: v })}
          />
          <TextInput
            label="Search"
            placeholder="Title or Creator..."
            leftSection={<IconSearch size={16} />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Button onClick={fetchData} variant="light" color="blue">
            Refresh
          </Button>
        </Group>
      </Paper>

      {/* Project List */}
      <Box pos="relative" mih={200}>
        <LoadingOverlay visible={loading} />

        {projects.length === 0 && !loading ? (
          <Paper p="xl" withBorder ta="center" bg="gray.0">
            <IconSearch size={40} color="gray" style={{ opacity: 0.5 }} />
            <Text c="dimmed" mt="sm">
              No projects match your filters
            </Text>
          </Paper>
        ) : (
          <Stack gap="md">
            {projects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                selected={selectedProjects.includes(project.id)}
                onSelect={(chk) =>
                  setSelectedProjects((p) =>
                    chk
                      ? [...p, project.id]
                      : p.filter((id) => id !== project.id),
                  )
                }
                onRefresh={fetchData}
              />
            ))}
          </Stack>
        )}

        {/* Pagination */}
        {projects.length > 0 && (
          <Group justify="center" mt="xl">
            <Pagination
              total={pagination.totalPages}
              value={pagination.page}
              onChange={(p) => setPagination((prev) => ({ ...prev, page: p }))}
            />
          </Group>
        )}
      </Box>
    </Stack>
  );
};

// =============================================================================
// PROJECT CARD COMPONENT
// =============================================================================

const ProjectCard = ({ project, selected, onSelect, onRefresh }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [createProdModal, setCreateProdModal] = useState(false);

  // Reusable format helper
  const formatTarget = () => {
    if (project.targetAmount)
      return `₹${project.targetAmount.toLocaleString()}`;
    if (project.targetQuantity) return `${project.targetQuantity} units`;
    if (project.targetVotes) return `${project.targetVotes} votes`;
    return "N/A";
  };

  const handleCreateProductSuccess = (data) => {
    showSuccess(
      "Product Created",
      `"${data.product.title}" is now in inventory.`,
    );
    setCreateProdModal(false);
    onRefresh();
  };

  return (
    <Card
      withBorder
      radius="md"
      padding="lg"
      className="hover:shadow-md transition-shadow"
    >
      <Group align="flex-start" wrap="nowrap">
        {/* Checkbox for Bulk Actions */}
        {project.status === "PENDING_APPROVAL" && (
          <Checkbox
            checked={selected}
            onChange={(e) => onSelect(e.currentTarget.checked)}
            mt={4}
          />
        )}

        {/* Image */}
        <Box
          w={120}
          h={90}
          bg="gray.1"
          style={{ borderRadius: 8, overflow: "hidden", flexShrink: 0 }}
        >
          {project.primaryImage ? (
            <Image src={project.primaryImage} w="100%" h="100%" fit="cover" />
          ) : (
            <Center h="100%">
              <ProjectTypeBadge type={project.projectType} />
            </Center>
          )}
        </Box>

        {/* Info */}
        <Stack gap={4} style={{ flex: 1 }}>
          <Group justify="space-between">
            <Text fw={600} size="lg" lineClamp={1}>
              {project.title}
            </Text>
            <Group gap="xs">
              {project.isUrgent && (
                <Badge color="red" variant="dot">
                  Urgent
                </Badge>
              )}
              <StatusBadge status={project.status} />
            </Group>
          </Group>

          <Text size="sm" c="dimmed" lineClamp={2}>
            {project.shortDescription}
          </Text>

          <Group gap="xl" mt="xs">
            <Group gap={4}>
              <IconInfoCircle size={14} className="text-gray-400" />
              <Text size="xs">{project.category}</Text>
            </Group>
            <Group gap={4}>
              <IconUser size={14} className="text-gray-400" />
              <Text size="xs">{project.creator?.email}</Text>
            </Group>
            <Group gap={4}>
              <IconChartBar size={14} className="text-gray-400" />
              <Text size="xs">Target: {formatTarget()}</Text>
            </Group>
          </Group>
        </Stack>

        {/* Actions */}
        <Stack gap="xs" align="flex-end">
          <Button
            size="xs"
            variant="light"
            onClick={() =>
              navigate(
                `/admin/dashboard/project-management/review/${project.id}`,
              )
            }
            leftSection={<IconEye size={14} />}
          >
            Review
          </Button>

          {project.status === "ACTIVE" && (
            <Button
              size="xs"
              color="green"
              variant="outline"
              onClick={() => setCreateProdModal(true)}
              leftSection={<IconShoppingCart size={14} />}
            >
              Create Product
            </Button>
          )}
        </Stack>
      </Group>

      {/* Modals */}
      <CreateProductModal
        isOpen={createProdModal}
        onClose={() => setCreateProdModal(false)}
        project={project}
        onSuccess={handleCreateProductSuccess}
      />
    </Card>
  );
};

// =============================================================================
// PROJECT REVIEW PAGE (DETAILS)
// =============================================================================

export const ProjectReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false); // New loading state for actions

  // Modal States
  const [modals, setModals] = useState({
    approve: false,
    reject: false,
    change: false,
    createProd: false,
    edit: false,
  });
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/admin/projects/review/${id}`,
          { headers: getAuthHeaders() },
        );
        const data = await res.json();
        if (data.success) setProject(data.data);
        else throw new Error(data.error);
      } catch (e) {
        showError("Error", "Failed to load details");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchDetails();
  }, [id]);

  // --- NEW: Handle Approval Logic ---
  const handleApprove = async () => {
    setActionLoading(true);
    try {
      // Reusing the bulk-approve endpoint for a single item to ensure consistency
      const res = await fetch(
        `${API_BASE_URL}/admin/projects/bulk-approve`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            projectIds: [project.id], // Send single ID in array
            adminNotes: "Approved from review page",
          }),
        },
      );
      const data = await res.json();

      if (data.success) {
        showSuccess("Approved", "Project is now live");
        setModals({ ...modals, approve: false });
        navigate("/admin/dashboard/project-management"); // Redirect back to list
      } else {
        showError("Approval Failed", data.error?.message || "Unknown error");
      }
    } catch (error) {
      console.error("Approval Error:", error);
      showError("Error", "Network request failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <LoadingOverlay visible />;
  if (!project) return <Text>Project not found</Text>;

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate("/admin/dashboard/project-management")}
        >
          Back to List
        </Button>
        <Group>
          {project.status === "PENDING_APPROVAL" && (
            <>
              <Button
                color="red"
                variant="light"
                onClick={() => setModals({ ...modals, reject: true })}
              >
                Reject
              </Button>
              <Button
                color="yellow"
                variant="light"
                onClick={() => setModals({ ...modals, change: true })}
              >
                Request Changes
              </Button>
              <Button
                color="green"
                onClick={() => setModals({ ...modals, approve: true })}
              >
                Approve Project
              </Button>
            </>
          )}
          {(project.status === "PENDING_APPROVAL" ||
            project.status === "ACTIVE") && (
            <Button
              color="blue"
              variant="outline"
              leftSection={<IconEdit size={16} />}
              onClick={() =>
                navigate(
                  `/admin/dashboard/project-management/edit/${project.id}`,
                )
              }
            >
              Edit Project
            </Button>
          )}
          {project.status === "ACTIVE" && (
            <Button
              color="teal"
              leftSection={<IconShoppingCart size={16} />}
              onClick={() => setModals({ ...modals, createProd: true })}
            >
              Create Product
            </Button>
          )}
        </Group>
      </Group>

      <Grid gutter="lg">
        {/* LEFT COLUMN: Main Info */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Stack gap="lg">
            {/* Title Card */}
            <Card withBorder radius="md" padding="xl">
              <Group justify="space-between" align="start">
                <Box>
                  <Group gap="sm" mb="xs">
                    <ProjectTypeBadge type={project.projectType} />
                    <Badge variant="dot">{project.category}</Badge>
                  </Group>
                  <Text size="2rem" fw={800} lh={1.2} mb="sm">
                    {project.title}
                  </Text>
                  <StatusBadge status={project.status} />
                </Box>
              </Group>
              <Divider my="lg" />
              <Text size="lg" fw={600} mb="xs">
                Description
              </Text>
              <Text c="dimmed" style={{ whiteSpace: "pre-wrap" }}>
                {project.description}
              </Text>
            </Card>

            {/* Media Gallery */}
            <Card withBorder radius="md" padding="lg">
              <Text fw={600} mb="md">
                Media Gallery ({project.media?.length || 0})
              </Text>
              <SimpleGrid cols={3}>
                {project.media?.map((m, i) => (
                  <Card key={i} padding="0" radius="md" withBorder>
                    <Image src={m.url} h={140} fit="cover" />
                    {m.caption && (
                      <Text size="xs" p="xs" lineClamp={1}>
                        {m.caption}
                      </Text>
                    )}
                  </Card>
                ))}
              </SimpleGrid>
            </Card>

            {/* Design Options */}
            {project.options?.length > 0 && (
              <Card withBorder radius="md" padding="lg">
                <Text fw={600} mb="md">
                  Options / Variations
                </Text>
                <Stack gap="sm">
                  {project.options.map((opt, i) => (
                    <Group
                      key={i}
                      p="sm"
                      bg="gray.0"
                      style={{ borderRadius: 8 }}
                    >
                      <Avatar src={opt.imageUrl} radius="md" size="lg" />
                      <Box style={{ flex: 1 }}>
                        <Text fw={600} size="sm">
                          {opt.optionName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {opt.description}
                        </Text>
                      </Box>
                      <Badge color="blue">{opt.voteCount} Votes</Badge>
                    </Group>
                  ))}
                </Stack>
              </Card>
            )}
          </Stack>
        </Grid.Col>

        {/* RIGHT COLUMN: Sidebar Stats */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack gap="md">
            {/* Analysis Card */}
            {project.analysis && (
              <Card
                withBorder
                radius="md"
                bg={project.analysis.score > 70 ? "green.0" : "orange.0"}
              >
                <Group justify="center" mb="md">
                  <RingProgress
                    size={80}
                    thickness={8}
                    sections={[
                      {
                        value: project.analysis.score,
                        color: project.analysis.score > 70 ? "green" : "orange",
                      },
                    ]}
                    label={
                      <Text ta="center" fw={700} size="sm">
                        {project.analysis.score}%
                      </Text>
                    }
                  />
                </Group>
                <Text ta="center" fw={600} size="lg" mb="sm">
                  Quality Score
                </Text>
                <Stack gap="xs">
                  {project.analysis.issues?.map((iss, i) => (
                    <Alert
                      key={i}
                      color="red"
                      variant="light"
                      icon={<IconAlertCircle size={14} />}
                      p="xs"
                    >
                      <Text size="xs">{iss}</Text>
                    </Alert>
                  ))}
                </Stack>
              </Card>
            )}

            {/* Project Stats */}
            <Card withBorder radius="md">
              <Text fw={600} mb="md">
                Campaign Details
              </Text>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Target Amount
                  </Text>
                  <Text fw={600}>
                    ₹{project.targetAmount?.toLocaleString() || 0}
                  </Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Target Votes
                  </Text>
                  <Text fw={600}>{project.targetVotes || 0}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Unit Price
                  </Text>
                  <Text fw={600}>₹{project.unitPrice || 0}</Text>
                </Group>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Shipping
                  </Text>
                  <Text fw={600}>₹{project.shippingFee || 0}</Text>
                </Group>
                <Divider />
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    Ends On
                  </Text>
                  <Text fw={600}>
                    {new Date(project.fundraisingEnd).toLocaleDateString()}
                  </Text>
                </Group>
              </Stack>
            </Card>

            {/* Creator Info */}
            <Card withBorder radius="md">
              <Group mb="md">
                <Avatar color="blue" radius="xl">
                  <IconUser size={20} />
                </Avatar>
                <Box>
                  <Text fw={600} size="sm">
                    Creator Info
                  </Text>
                  <Text size="xs" c="dimmed">
                    {project.creator?.email}
                  </Text>
                </Box>
              </Group>
              <Group grow gap="xs">
                <Stack
                  gap={0}
                  align="center"
                  bg="gray.0"
                  p="xs"
                  style={{ borderRadius: 8 }}
                >
                  <Text fw={700}>{project.creator?.totalProjects || 0}</Text>
                  <Text size="xs" c="dimmed">
                    Projects
                  </Text>
                </Stack>
                <Stack
                  gap={0}
                  align="center"
                  bg="green.0"
                  p="xs"
                  style={{ borderRadius: 8 }}
                >
                  <Text fw={700} c="green">
                    {project.creator?.successRate || 0}%
                  </Text>
                  <Text size="xs" c="green.8">
                    Success
                  </Text>
                </Stack>
              </Group>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* --- CONFIRMATION MODALS --- */}

      {/* Approval Modal */}
      <Modal
        opened={modals.approve}
        onClose={() => setModals({ ...modals, approve: false })}
        title="Confirm Approval"
      >
        <Text mb="md">
          Are you sure you want to approve <b>{project.title}</b>? This will
          make it live immediately.
        </Text>
        <Button
          fullWidth
          color="green"
          loading={actionLoading}
          onClick={handleApprove}
        >
          Confirm Approval
        </Button>
      </Modal>

      <CreateProductModal
        isOpen={modals.createProd}
        onClose={() => setModals({ ...modals, createProd: false })}
        project={project}
        onSuccess={() => {
          showSuccess("Created", "Product created");
          setModals({ ...modals, createProd: false });
        }}
      />
    </Box>
  );
};
